import { TFunction } from 'i18next'

interface BaseValidationRule {
  required?: string
  validate?: (value: unknown) => string | boolean
}

interface LengthValidationRule extends BaseValidationRule {
  minLength?: { value: number; message: string }
  maxLength?: { value: number; message: string }
}

interface PatternValidationRule extends BaseValidationRule {
  pattern: { value: RegExp; message: string }
}

export type ValidationRule = BaseValidationRule &
  Partial<LengthValidationRule & PatternValidationRule>

export const required = (
  t: TFunction,
  fieldName: string,
  customMessage?: string,
): ValidationRule => ({
  required: customMessage ?? t(`${fieldName} is required`),
  validate: (value: unknown) =>
    typeof value === 'string' && value.trim()
      ? true
      : t(`${fieldName} cannot be empty`),
})

export const maxLength = (
  t: TFunction,
  fieldName: string,
  max: number,
): ValidationRule => ({
  maxLength: {
    value: max,
    message: t(`${fieldName} must be at most ${max} characters`),
  },
})

export const minLength = (
  t: TFunction,
  fieldName: string,
  min: number,
): ValidationRule => ({
  minLength: {
    value: min,
    message: t(`${fieldName} must be at least ${min} characters`),
  },
})

export const pattern = (
  t: TFunction,
  pattern: RegExp,
  message: string,
): ValidationRule => ({
  pattern: {
    value: pattern,
    message: t(message),
  },
})

// Common validation patterns
export const email = (t: TFunction): ValidationRule => ({
  pattern: {
    value: /^[\w\-.]+@[\w-]+\.+[\w-]{2,4}$/,
    message: t('Invalid email address'),
  },
})

export const phone = (t: TFunction): ValidationRule => ({
  minLength: {
    value: 14,
    message: t('Invalid phone number'),
  },
  pattern: {
    value: /^\(\d{3}\) \d{3}-\d{4}$/,
    message: t('Invalid phone number format'),
  },
})

export const zipCode = (t: TFunction): ValidationRule => ({
  required: t('Zip code is required'),
  validate: (value: unknown) => {
    if (typeof value !== 'string' || !value.trim()) {
      return t('Zip code cannot be empty')
    }
    return value.length === 5 || t('Invalid zip code')
  },
})

export const password = (t: TFunction): ValidationRule => ({
  required: t('Password is required'),
  minLength: {
    value: 8,
    message: t('Password must be at least 8 characters'),
  },
  pattern: {
    value: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/,
    message: t(
      'Password must contain uppercase, lowercase, number and special character',
    ),
  },
})

export const url = (t: TFunction): ValidationRule => ({
  pattern: {
    value:
      /^(https?:\/\/)?([a-z0-9]([a-z0-9-]*[a-z0-9])?\.)+[a-z]{2,}(\/[\w.-]*)*(\?[\w=&%.-]*)?$/i,
    message: t('Invalid URL'),
  },
})

export const numeric = (t: TFunction, fieldName: string): ValidationRule => ({
  pattern: {
    value: /^\d+$/,
    message: t(`${fieldName} must be numeric`),
  },
})

export const decimal = (t: TFunction, fieldName: string): ValidationRule => ({
  pattern: {
    value: /^\d*\.?\d*$/,
    message: t(`${fieldName} must be a decimal number`),
  },
})

export const range = (
  t: TFunction,
  fieldName: string,
  min: number,
  max: number,
): ValidationRule => ({
  validate: (value: unknown) => {
    const num = Number(value)
    if (isNaN(num)) return t(`${fieldName} must be a number`)
    if (num < min) return t(`${fieldName} must be at least ${min}`)
    if (num > max) return t(`${fieldName} must be at most ${max}`)
    return true
  },
})

// Helper function to combine multiple validation rules
export const combine = (...rules: ValidationRule[]): ValidationRule => {
  return rules.reduce((acc, rule) => ({ ...acc, ...rule }), {})
}
